/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "General models", description = "Make warehouse data available to agents without adding it to audience building or sync workflows.", lastUpdatedDate = new Date(1787788800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "General models",
        description: "Make warehouse data available to agents without adding it to audience building or sync workflows.",
        lastUpdatedDate: "Thu Aug 27 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Decide whether to use a general model",
          "depth": 0,
          "id": "decide-whether-to-use-a-general-model"
        }, {
          "value": "How general models work",
          "depth": 0,
          "id": "how-general-models-work"
        }, {
          "value": "Add general models",
          "depth": 0,
          "id": "add-general-models"
        }, {
          "value": "1. Open general models",
          "depth": 1,
          "id": "1-open-general-models"
        }, {
          "value": "2. Choose warehouse tables",
          "depth": 1,
          "id": "2-choose-warehouse-tables"
        }, {
          "value": "3. Name and describe each model",
          "depth": 1,
          "id": "3-name-and-describe-each-model"
        }, {
          "value": "4. Review the columns agents can query",
          "depth": 1,
          "id": "4-review-the-columns-agents-can-query"
        }, {
          "value": "Control what agents can access",
          "depth": 0,
          "id": "control-what-agents-can-access"
        }, {
          "value": "Model permissions",
          "depth": 1,
          "id": "model-permissions"
        }, {
          "value": "Column privacy",
          "depth": 1,
          "id": "column-privacy"
        }, {
          "value": "Query general models with agents",
          "depth": 0,
          "id": "query-general-models-with-agents"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      code: "code",
      ul: "ul",
      li: "li",
      h3: "h3",
      img: "img",
      blockquote: "blockquote"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsx(FeatureFlag, {
        featureName: "General models"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["A connected data ", _jsx(_components.a, {
                href: "/sources/overview",
                children: "source"
              }), " such as Snowflake, BigQuery, or Databricks"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsxs(_components.em, {
          children: ["General models make warehouse data available to ", _jsx(_components.a, {
            href: "/agents/overview",
            children: "agents"
          }), " when that data doesn't belong in audience building or sync workflows. Use them for tables such as sales data, product catalogs, web analytics, or competitive datasets that agents need to answer questions."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "decide-whether-to-use-a-general-model",
        children: "Decide whether to use a general model"
      }), "\n", _jsx(_components.p, {
        children: "Agents can already query the parent, related, and event models in your Customer Studio schema. You only need a general model when an agent needs to query a warehouse table that isn't part of the Customer Studio schema and that marketers shouldn't use to build or sync audiences."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, suppose marketers build audiences of customers from a ", _jsx(_components.code, {
          children: "Users"
        }), " parent model. An agent answering questions about those customers can already use the user attributes, purchases, and events connected to that schema."]
      }), "\n", _jsx(_components.p, {
        children: "If the same agent also needs to answer:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "How did website traffic change after the campaign?"
        }), "\n", _jsx(_components.li, {
          children: "Which products had the largest increase in sales?"
        }), "\n", _jsx(_components.li, {
          children: "How do our results compare with an external benchmark?"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "you can expose the web analytics, sales, or benchmark tables that hold that data as general models. Agents can query those tables without them appearing as options in the audience builder."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "If the data should..."
            }), _jsx(_components.th, {
              children: "Use..."
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Represent the people, accounts, or other records marketers build audiences of"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/schema#create-the-parent-model",
                children: "Parent model"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Add records associated with an audience member, such as purchases or subscriptions"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/schema#add-a-related-model",
                children: "Related model"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["Represent timestamped behavior, such as ", _jsx(_components.code, {
                children: "Product Viewed"
              })]
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/schema#add-an-event-model",
                children: "Event model"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["Be available to agents but ", _jsx(_components.strong, {
                children: "not"
              }), " used to build or sync audiences"]
            }), _jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "General model"
              })
            })]
          })]
        })]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Don't create a general model for a table that's already available through your\nCustomer Studio schema. Agents can query existing parent, related, and event\nmodels."
        })
      }), "\n", _jsx(_components.h2, {
        id: "how-general-models-work",
        children: "How general models work"
      }), "\n", _jsx(_components.p, {
        children: "A general model makes a warehouse table queryable by agents. For each general model, you can configure:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "A name and description"
          }), " that explain what the data represents."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Column visibility and privacy"
          }), " that set which columns agents can query or return."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Permissions"
          }), " that restrict access by user or group."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can also add several warehouse tables at once with bulk creation."
      }), "\n", _jsx(_components.p, {
        children: "Unlike a parent model, a general model doesn't require a primary key, because Hightouch doesn't use it to represent individual audience members or sync records."
      }), "\n", _jsx(_components.p, {
        children: "General models don't appear in the audience builder or as models you can configure for audience syncs."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "add-general-models",
        children: "Add general models"
      }), "\n", _jsx(_components.h3, {
        id: "1-open-general-models",
        children: "1. Open general models"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Customer Studio > Schema"
        }), ", then open ", _jsx(_components.strong, {
          children: "General models"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/general-models/open-general-models.png",
          alt: "Schema page with an arrow pointing to the General models button in the header"
        })
      }), "\n", _jsx(_components.h3, {
        id: "2-choose-warehouse-tables",
        children: "2. Choose warehouse tables"
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Add general model"
        }), ". Browse or search your warehouse, then select one or more tables that agents should be able to query."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/general-models/add-general-models.png",
          alt: "Add general models wizard — select tables step"
        })
      }), "\n", _jsx(_components.p, {
        children: "Only add tables that agents need to answer expected questions. You don't need to expose every table in your warehouse."
      }), "\n", _jsx(_components.h3, {
        id: "3-name-and-describe-each-model",
        children: "3. Name and describe each model"
      }), "\n", _jsx(_components.p, {
        children: "For each table, review its name and optionally add a description."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/general-models/general-models-add-details.png",
          alt: "Add general models wizard — add details step"
        })
      }), "\n", _jsx(_components.p, {
        children: "Describe the table in terms of what it contains and when an agent should use it. For example:"
      }), "\n", _jsxs(_components.blockquote, {
        children: ["\n", _jsx(_components.p, {
          children: "Daily website sessions and conversions by marketing channel. Use this model for questions about traffic, channel performance, and conversion trends."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["This tells the agent what the table contains and when to use it. A warehouse name such as ", _jsx(_components.code, {
          children: "fct_web_daily"
        }), " doesn't."]
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Create models"
        }), " when you're finished."]
      }), "\n", _jsx(_components.h3, {
        id: "4-review-the-columns-agents-can-query",
        children: "4. Review the columns agents can query"
      }), "\n", _jsxs(_components.p, {
        children: ["After creating a model, select it from the ", _jsx(_components.strong, {
          children: "General models"
        }), " list."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/general-models/general-models-panel.png",
          alt: "General models panel listing added models with the Add general model button"
        })
      }), "\n", _jsx(_components.p, {
        children: "Review:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Columns"
          }), " — choose which columns agents can query or return, and add descriptions where the meaning isn't obvious."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Description"
          }), " — explain what the model represents and the kinds of questions it can help answer."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/general-models/general-models-view.png",
          alt: "Column detail view with enable toggles and descriptions"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Use names and descriptions that match how your organization refers to the data, where warehouse terminology would be unclear. For example, describe ", _jsx(_components.code, {
          children: "net_rev_30d"
        }), " as \"Net revenue generated in the last 30 days\" if that's what the column represents."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Explain units, time windows, or important distinctions that an agent couldn't\ninfer from the column name alone, rather than restating the name."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "control-what-agents-can-access",
        children: "Control what agents can access"
      }), "\n", _jsx(_components.p, {
        children: "What an agent can access depends on who's asking. An agent can't use a model or column that the current user isn't allowed to access."
      }), "\n", _jsx(_components.h3, {
        id: "model-permissions",
        children: "Model permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["Adding, editing, or deleting general models requires the ", _jsx(_components.strong, {
          children: "Configure schema"
        }), " grant on the source."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use the ", _jsx(_components.strong, {
          children: "View model data"
        }), " permission to control who can query each general model. General models appear in the ", _jsx(_components.strong, {
          children: "Models"
        }), " tab when you configure ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "user groups and roles"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/general-models/gm-workspace-settings.png",
          alt: "General models permissions in the role configuration panel"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If a user doesn't have ", _jsx(_components.strong, {
          children: "View model data"
        }), " access to a general model, the agent can't query that model for the user."]
      }), "\n", _jsx(_components.h3, {
        id: "column-privacy",
        children: "Column privacy"
      }), "\n", _jsx(_components.p, {
        children: "Column privacy controls whether agents can query or return individual column values. Depending on the column's privacy level, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "prevent agents from using the column"
        }), "\n", _jsx(_components.li, {
          children: "allow agents to filter on the column without seeing its raw values"
        }), "\n", _jsx(_components.li, {
          children: "allow agents to use and return the column's values"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Configure these settings from the column detail view."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/general-models/gm-columns-disabled.png",
          alt: "Column detail view with some columns disabled"
        })
      }), "\n", _jsx(_components.p, {
        children: "Use stricter access for sensitive fields that agents don't need to return directly. For example, an agent may need to filter records by a sensitive attribute without displaying that attribute's raw value."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "query-general-models-with-agents",
        children: "Query general models with agents"
      }), "\n", _jsx(_components.p, {
        children: "You don't choose a general model before asking an agent a question. When you ask a question, agents can query the models you have permission to access and select the data relevant to the request."
      }), "\n", _jsx(_components.p, {
        children: "For example, if you ask:"
      }), "\n", _jsxs(_components.blockquote, {
        children: ["\n", _jsx(_components.p, {
          children: "Which marketing channels drove the largest increase in website conversions last month?"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "the agent can query a general model containing channel-level web analytics if that model is available to you."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/general-models/gm-agent-query-response.png",
          alt: "Agent querying a general model and returning a summary with data table"
        })
      }), "\n", _jsx(_components.p, {
        children: "Open the response details to see which models and warehouse queries the agent used."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/general-models/gm-agent-query-details.png",
          alt: "Response details showing the agent's reasoning steps and warehouse query"
        })
      }), "\n", _jsx(_components.p, {
        children: "If an agent repeatedly selects the wrong model or maps a term to the wrong field, review the model and column names, descriptions, and available models. Overlapping or unclear descriptions can cause agents to select the wrong dataset."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/data-model#general-models",
            children: "Plan your data model"
          }), " to decide which warehouse data belongs in audience building."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/schema",
            children: "Define your schema"
          }), " to configure parent, related, and event models."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/agents/overview",
            children: "Agents overview"
          }), " to learn how agents query the data available to them."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/roles",
            children: "Roles"
          }), " to control access to model data."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
